import ApiClient from '/src/services/ApiClient'
import { cloneDeep } from 'lodash'

class ApiService {
  /**
   *
   * @param {string} facebookId
   * @param {string} email
   * @param {string} [nickname]
   * @returns {Promise<HttpResponse>}
   */
  createOrGetUser ({ facebookId, email, nickname }) {
    return ApiClient.post('/users', { facebookId, email, nickname })
  }

  /**
   *
   *
   * @param {Object} data
   * @param {string} data.email
   * @param {string} data.givenName
   * @param {string} data.familyName
   * @param {string} data.authorizationCode
   * @param {string} data.identityToken
   * @param {string} data.user
   * @returns {Promise<HttpResponse>}
   */
  createOrGetUserWithApple (data) {
    return ApiClient.post('/users/apple', data)
  }

  /**
   * Get 3i JWT token for user
   * @param {string} userId
   * @return {Promise<HttpResponse>}
   **/
  get3iToken (userId) {
    return ApiClient.post(`/auth/3i-token`, { user_id: userId })
  }

  getUser (id) {
    return ApiClient.get(`/users/${id}`)
  }

  deleteUser (id) {
    return ApiClient.delete(`/users/${id}`)
  }

  /**
   * Update user nickname and show_picture
   * @param id
   * @param user
   * @returns {Promise<*|Promise<Awaited<unknown>>>}
   */
  updateUser (id, user) {
    return ApiClient.put(`/users/${id}`, user)
  }

  /**
   * Update user settings. onboarding.
   *
   * @param id
   * @param {Object} settings
   * @param {boolean} settings.onboarding
   * @returns {Promise<*|Promise<Awaited<unknown>>>}
   */
  updateUserSettings (id, settings) {
    return ApiClient.put(`/users/${id}/settings`, settings)
  }

  saveOnboarding ({ user_id, answers }) {
    return ApiClient.post('/onboarding', { user_id, answers })
  }

  // eslint-disable-next-line camelcase
  getLeaderboard (params) {
    const p = cloneDeep(params)

    if (p.facebook_ids && Array.isArray(p)) {
      p.facebook_ids = p.facebook_ids.join(',')
    }

    return ApiClient.get('/users/top', { params: p })
  }

  updateSeenQuestions (userId, questions) {
    return ApiClient.put(`/users/${userId}/game_questions`, { questions })
  }

  updateSeenTopics (userId, topics, topic_pages, school_questions) {
    return ApiClient.put(`/users/${userId}/topics`, { topics, topic_pages, school_questions })
  }

  updateSeenTopicPages (userId, topic_pages) {
    return ApiClient.put(`/users/${userId}/topic_pages`, { topic_pages })
  }

  updateScores (userId, scores) {
    return ApiClient.put(`/users/${userId}/scores`, scores)
  }

  updateUserJourney (journeyId, journey) {
    return ApiClient.post(`/journey/days/${journeyId}`, journey)
  }

  createUserJourney (journey) {
    return ApiClient.put(`/journey/days`, journey)
  }

  /**
   * @typedef {Object} UserStreak
   * @property {number} id
   * @property {number|null} user_id
   * @property {string} app_instance_id
   * @property {string} started_at
   * @property {number} started_by_id
   * @property {number} current_day_ordinal
   * @property {string} current_day_completed_at
   * @property {number} current_day_id
   * @property {number} completed_days
   * @property {string|null} completed_at
   * @property {number|null} completed_by_id
   * @property {string} created_at
   * @property {string} updated_at
   */

  /**
   * Get user streak
   * @param user_id
   * @returns {Promise<Awaited<{data: UserStreak}>>}
   */
  getUserStreak ({ user_id }) {
    return ApiClient.post(`/journey/streak`, { user_id })
  }

  /**
   * @typedef {Object} UserJourneyDay
   * @property {number} id
   * @property {number} user_id
   * @property {string} app_instance_id
   * @property {number} journey_day_id
   * @property {number} journey_day_ordinal
   * @property {Object} journey_data
   * @property {number} streak_id
   * @property {boolean} streak_started
   * @property {boolean} streak_completed
   * @property {string} completed_at
   * @property {string} started_at
   *
   *
   * Fetch current user journey days
   *
   * @param {Number} [user_id] Current user ID if authenticated
   * @param {String} app_instance_id Alternatively an app instance. It is also passed as request header.
   * @returns {Promise<Awaited<{data: UserJourneyDay[]}>>}
   */
  searchUserJourneyDays ({ user_id, app_instance_id}) {
    return ApiClient.post(`/journey/search`, { user_id, app_instance_id })
      .then((res) => {
        res.data.sort((a, b) => a.journey_day_ordinal - b.journey_day_ordinal)
        return res
      }).then((res) => {
        // decode the journey data
        res.data.forEach(journey => {
          try {
            if (typeof journey.journey_data === 'string') {
              journey.journey_data = JSON.parse(journey.journey_data)
            }
          } catch (e) {}
        })

        return res
      })
  }

  getUserJourneyStreak({ user_id, app_instance_id }) {
    return ApiClient.post(`/journey/streak`, {
        user_id,
        app_instance_id
      })
      .then((res) => {
        res.data.sort((a, b) => a.journey_day_ordinal - b.journey_day_ordinal)
        return res
      })
  }

  /**
   * Update will also create a new instance if it doesn't exist
   *
   * @param {string} id
   * @param {Object} data
   * @returns {Promise<HttpResponse>}
   */
  updateAppInstance (id, data) {
    return ApiClient.put(`/apps/${id}`, data)
  }

  /**
   * Explicitly save appsflyer conversion data
   * @param appId
   * @param data
   * @returns {Promise<HttpResponse>}
   */
  registerAppsflyerConversion (appId, data) {
    return ApiClient.put(`/apps/${appId}/appsflyer`, data)
  }

  /**
   * Log app event
   * @param data
   * @returns {Promise<HttpResponse>}
   */
  createAppEvent (data) {
    return ApiClient.post('/events', data)
  }

  /**
   * Get remote config
   * @returns {Promise<HttpResponse>}
   */
  getRemoteConfig (options = {}) {
    return ApiClient.get('/remote-config', options)
  }

  /**
   * Request ad for display
   * @param placement
   * @param appInstanceId
   * @param options
   * @returns {Promise<HttpResponse>}
   */
  getPromo ({ placement, appInstanceId }, options = {}) {
    return ApiClient.post(`/promo/show`, { placement, 'app_instance_id': appInstanceId }, options)
  }
}

export default new ApiService()
